/* online-vet-advice.js — vanilla progressive enhancement for the Online Vet Advice (Nova) page.
 * Replaces the framework runtime from the React prototype:
 *   1. the interactive Nova chat (#sn-online-vet-advice [data-sn-widget="NovaChat"])
 *      — profile switch, topic → typed reply with a severity badge
 * No dependencies. Self-contained. Safe to load with `defer`.
 *
 * The page renders a faithful static snapshot server-side; this script enhances it.
 * If the script never runs, the static chat shell + FAQ questions are still visible.
 */
(function () {
  'use strict';

  // <html lang> comes from request.locale.iso_code, so /de/ pages report "de".
  var SN_DE = typeof document !== 'undefined' && document.documentElement
    ? /^de/i.test(document.documentElement.getAttribute('lang') || '')
    : false;
  var T = function (en, de) { return SN_DE ? de : en; };

  // ---- Chat data (ported verbatim from NovaChat.jsx) -----------------------
  var NV_PROFILES = [
    { id: 'bailey', name: 'Bailey', meta: '12 yr · spaniel', species: 'dog', bg: '#FFDDBF',
      note: T('At 12, I lean cautious - a senior dog’s symptoms can move faster.', 'Mit 12 bin ich vorsichtig - bei einem Seniorhund können Symptome schneller kippen.') },
    { id: 'pip', name: 'Pip', meta: '1 yr · pup', species: 'dog', bg: '#E6FFC6',
      note: T('A young, healthy pup - but small bodies dehydrate quickly, so I watch fluids.', 'Ein junger, gesunder Welpe - kleine Körper dehydrieren aber schnell, deshalb achte ich auf die Flüssigkeit.') },
    { id: 'mochi', name: 'Mochi', meta: '5 yr · cat', species: 'cat', bg: '#CFFF90',
      note: T('Cats hide illness brilliantly, so I flag the cat-specific dangers.', 'Katzen verbergen Krankheit hervorragend, deshalb weise ich auf die katzenspezifischen Gefahren hin.') },
  ];

  var NV_LEVELS = {
    monitor: { label: T('Monitor at home', 'Zu Hause beobachten'), color: '#5B8C3E', bg: '#E6FFC6' },
    soon:    { label: T('Worth a vet call', 'Anruf lohnt sich'), color: '#E07A3E', bg: '#FFE3CE' },
    now:     { label: T('Call your vet now', 'Jetzt Tierarzt anrufen'), color: '#C5462E', bg: '#FBD9D0' },
  };

  // each topic → answer(profile) = { level, paras: [] }
  var NV_TOPICS = [
    { id: 'vomit', label: T('Been sick twice', 'Hat zweimal erbrochen'), q: T("They've been sick twice and are off their food - should I worry?", 'Mein Tier hat zweimal erbrochen und frisst nicht - muss ich mir Sorgen machen?'),
      a: function (p) {
        if (p.species === 'cat') return { level: 'soon', paras: [
          (SN_DE ? 'Katzen verbergen Krankheit gut, ' + p.name + ' eingeschlossen - zweimal Erbrechen plus Futterverweigerung gehört noch am selben Tag angerufen, besonders wenn sie auch nicht trinkt oder das Klo nicht benutzt hat.' : "Cats hide illness well, " + p.name + " included - so two vomits plus going off food is worth a same-day call, especially if she's also not drinking or hasn't used the tray."),
          T("In the meantime: take food away for a few hours but always leave water, and watch for lethargy or any straining. If she perks up and eats, it may have been a passing upset.", 'In der Zwischenzeit: Futter für ein paar Stunden wegnehmen, Wasser aber immer stehen lassen, und auf Teilnahmslosigkeit oder Pressen achten. Wird sie munter und frisst, war es vielleicht eine vorübergehende Verstimmung.'),
        ] };
        if (p.id === 'bailey') return { level: 'soon', paras: [
          T("At 12, I'd be more careful than I would with a pup. Repeated vomiting in a senior dog can dehydrate quickly and sometimes points to something underlying.", 'Mit 12 wäre ich vorsichtiger als bei einem Welpen. Wiederholtes Erbrechen kann bei einem Seniorhund schnell zu Austrocknung führen und deutet manchmal auf eine Grunderkrankung hin.'),
          T("Hold food for 6–12 hours, offer small sips of water, then a little bland food. But if Bailey keeps being sick, won't keep water down, or seems flat, ring your vet today rather than waiting it out.", 'Futter 6–12 Stunden aussetzen, kleine Schlucke Wasser anbieten, dann etwas Schonkost. Erbricht Bailey weiter, behält kein Wasser oder wirkt matt, rufen Sie heute Ihre Praxis an, statt abzuwarten.'),
        ] };
        return { level: 'monitor', paras: [
          T("Two vomits in a young, bright dog like Pip is often just a passing tummy upset. Hold food for 6–12 hours, offer small sips of water, then reintroduce a small bland meal (plain chicken and rice).", 'Zweimal Erbrechen bei einem jungen, munteren Hund wie Pip ist oft nur eine vorübergehende Magenverstimmung. Futter 6–12 Stunden aussetzen, kleine Schlucke Wasser anbieten, dann eine kleine Schonkost-Mahlzeit (nur Hühnchen und Reis).'),
          T("Watch for red flags: repeated vomiting, blood, a bloated hard tummy, or going quiet and floppy. Any of those, or no improvement by morning - call your vet.", 'Achten Sie auf Warnzeichen: wiederholtes Erbrechen, Blut, ein aufgeblähter harter Bauch oder Teilnahmslosigkeit. Bei einem davon - oder wenn es bis morgen nicht besser ist - rufen Sie Ihre Praxis an.'),
        ] };
      } },
    { id: 'limp', label: T('Sudden limp', 'Plötzlich lahm'), q: T("They've come up with a sudden limp.", 'Mein Tier lahmt plötzlich.'),
      a: function (p) { return { level: 'soon', paras: [
        T("A mild limp that's already improving can often be rested for 24 hours - quiet lead walks only, no jumping or stairs.", 'Ein leichtes Lahmen, das schon besser wird, kann man oft 24 Stunden schonen - nur ruhige Spaziergänge an der Leine, kein Springen, keine Treppen.'),
        p.id === 'bailey'
          ? T("That said, at Bailey's age stiffness and limps can be arthritis or something in the joint, so if it's not clearly better tomorrow, book a check.", 'In Baileys Alter können Steifheit und Lahmen allerdings Arthrose oder ein Gelenkproblem sein - ist es morgen nicht deutlich besser, machen Sie einen Termin.')
          : (SN_DE ? 'Belastet ' + p.name + ' das Bein gar nicht, jault oder ist es morgen nicht besser, gehen Sie in die Praxis - es könnte verstaucht, gerissen oder gebrochen sein.' : "But if " + p.name + " won't put any weight on it, is crying out, or it's no better tomorrow, see your vet - something could be sprained, torn or broken."),
      ] }; } },
    { id: 'lump', label: T('Found a lump', 'Knoten entdeckt'), q: T("I've just found a lump on them.", 'Ich habe gerade einen Knoten entdeckt.'),
      a: function (p) { return { level: 'soon', paras: [
        T("Take a breath - most lumps turn out to be harmless. But a new lump always deserves a (non-urgent) vet check, because the only way to know what it is, is to have it looked at.", 'Erst einmal durchatmen - die meisten Knoten sind harmlos. Ein neuer Knoten gehört aber immer (nicht dringend) angesehen, denn nur so lässt sich klären, was es ist.'),
        T("Note its size, where it is, and whether it's soft or firm, so you can track any change. It's not a 2am emergency unless it's hot, painful, bleeding or growing fast.", 'Notieren Sie Größe, Stelle und ob er weich oder fest ist, damit Sie Veränderungen verfolgen können. Ein Notfall um zwei Uhr nachts ist es nur, wenn er heiß, schmerzhaft, blutend oder schnell wachsend ist.'),
      ] }; } },
    { id: 'para', label: T('Can I give paracetamol?', 'Darf ich Paracetamol geben?'), q: T('Can I give them paracetamol for the pain?', 'Darf ich gegen die Schmerzen Paracetamol geben?'),
      a: function (p) {
        if (p.species === 'cat') return { level: 'now', paras: [
          T("Stop - never give paracetamol to a cat. It is highly toxic to cats and can be fatal even in a tiny amount.", 'Stopp - geben Sie einer Katze niemals Paracetamol. Es ist für Katzen hochgiftig und kann selbst in kleinster Menge tödlich sein.'),
          T("If Mochi has already had any, treat it as an emergency and call your vet or an emergency line right now. Don't wait for symptoms.", 'Hat Mochi bereits etwas davon bekommen, behandeln Sie es als Notfall und rufen Sie sofort Ihre Praxis oder den tierärztlichen Notdienst an. Warten Sie nicht auf Symptome.'),
        ] };
        return { level: 'soon', paras: [
          (SN_DE ? 'Nur wenn Ihre Praxis es ausdrücklich angeordnet hat, in der genau für das Gewicht von ' + p.name + ' festgelegten Dosis - niemals eigenmächtig aus dem Schrank. Der sichere Bereich ist schmal, und viele Schmerzmittel für Menschen (besonders Ibuprofen) sind für Hunde giftig.' : "Only ever if your vet has specifically directed it, at the exact dose they set for " + p.name + "'s weight - never from the cupboard on your own. The safe margin is small, and many human painkillers (ibuprofen especially) are toxic to dogs."),
          (SN_DE ? 'Sagen Sie mir, was wehtut und wie es ' + p.name + ' ansonsten geht, dann helfe ich Ihnen zu entscheiden, ob Sie bis zum Termin warten können oder besser jetzt anrufen. Über das Schmerzmittel selbst entscheidet aber die Praxis.' : "Tell me what's hurting and how " + p.name + " is otherwise, and I'll help you decide whether it's safe to wait for the vet or worth a call now. But the painkiller itself is a vet's call."),
        ] };
      } },
    { id: 'home', label: T('Can I treat it at home?', 'Kann ich das zu Hause behandeln?'), q: T('Can I just treat this at home without a vet?', 'Kann ich das einfach zu Hause ohne Tierarzt behandeln?'),
      a: function (p) { return { level: 'monitor', paras: [
        T("Sometimes, yes - plenty of minor things can be monitored with simple supportive care. But guessing with the wrong remedy can make things worse and cost more in the end.", 'Manchmal ja - viele Kleinigkeiten lassen sich mit einfacher Unterstützung beobachten. Mit dem falschen Mittel zu experimentieren kann es aber verschlimmern und am Ende teurer werden.'),
        (SN_DE ? 'Sagen Sie mir das genaue Symptom und wie lange es besteht, dann gebe ich Ihnen eine ehrliche Einschätzung: Was sich bei ' + p.name + ' zu Hause beobachten lässt, welche einfachen Schritte helfen und welche Zeichen wirklich in die Praxis gehören.' : "Tell me the exact symptom and how long it's been going, and I'll help you judge honestly: what's safe to watch at home for " + p.name + ", what simple steps help, and which signs are red flags that need a real vet."),
      ] }; } },
    { id: 'flea', label: T('Flea & worming', 'Floh- & Wurmkur'), q: T('Do I need a prescription for flea treatment?', 'Brauche ich für Flohmittel ein Rezept?'),
      a: function (p) { return { level: 'monitor', paras: [
        (SN_DE ? 'Es ist wirklich unübersichtlich: Manche wirksamen Mittel sind verschreibungspflichtig, andere apothekenpflichtig und wieder andere frei verkäuflich - und die richtige Wahl hängt von Gewicht, Alter und Gesundheit von ' + p.name + ' ab.' : "It's genuinely confusing: some effective treatments are prescription-only (POM-V), others are over-the-counter, and the right choice depends on " + p.name + "'s weight, age and health."),
        T("I'll help you understand which is appropriate so you're not guessing in a pet-shop aisle - then you can set it on a schedule so you never miss a dose.", 'Ich helfe Ihnen einzuschätzen, was passt, damit Sie im Regal nicht raten müssen - danach legen Sie es in einen Rhythmus und verpassen keine Gabe mehr.'),
      ] }; } },
  ];

  function greeting(p) {
    return [{ from: 'nova', greet: true, paras: [
      "Hi, I'm Nova. I've got " + p.name + "'s profile open - " +
      p.meta.split('·')[1].trim() + ". Tell me what's worrying you, or tap a question below. I'll help you decide what to do - I won't replace your vet.",
    ] }];
  }

  // ---- SVG fragments (ported from illustrations.jsx) -----------------------
  function orb(size) {
    return '<svg width="' + size + '" height="' + size + '" viewBox="0 0 120 120" fill="none" aria-hidden="true">' +
      '<circle cx="60" cy="60" r="56" fill="#224031"></circle>' +
      '<circle cx="60" cy="60" r="56" fill="none" stroke="#CFFF90" stroke-width="3" stroke-dasharray="6 9" opacity="0.7"></circle>' +
      '<circle cx="44" cy="54" r="7" fill="#CFFF90"></circle><circle cx="76" cy="54" r="7" fill="#CFFF90"></circle>' +
      '<circle cx="46" cy="56" r="2.6" fill="#224031"></circle><circle cx="78" cy="56" r="2.6" fill="#224031"></circle>' +
      '<path d="M44 74c5 6 27 6 32 0" stroke="#CFFF90" stroke-width="5" stroke-linecap="round" fill="none"></path>' +
      '<path d="M92 30l2.4 6.4L101 39l-6.6 2.4L92 48l-2.4-6.6L83 39l6.6-2.6Z" fill="#CFFF90"></path></svg>';
  }
  function pawDisc(size, bg) {
    var paw = size * 0.5;
    return h('span', {
      css: 'width:' + size + 'px;height:' + size + 'px;border-radius:999px;background:' + bg +
        ';display:inline-flex;align-items:center;justify-content:center;flex-shrink:0',
      html: '<svg width="' + paw + '" height="' + paw + '" viewBox="0 0 24 24" fill="var(--sn-forest)" aria-hidden="true">' +
        '<ellipse cx="7" cy="9" rx="2" ry="2.6"></ellipse>' +
        '<ellipse cx="12" cy="7.4" rx="2" ry="2.7"></ellipse>' +
        '<ellipse cx="17" cy="9" rx="2" ry="2.6"></ellipse>' +
        '<path d="M12 11.2c2.7 0 4.8 2 4.8 4.2 0 1.8-1.6 2.8-3.3 2.5-.6-.1-1.2-.1-1.5-.1s-.9 0-1.5.1c-1.7.3-3.3-.7-3.3-2.5 0-2.2 2.1-4.2 4.8-4.2Z"></path></svg>',
    });
  }
  var ICON_PATHS = {
    arrow: '<path d="M5 12h14M13 6l6 6-6 6"></path>',
    sparkle: '<path d="M12 3l1.8 4.9L18.7 9.7l-4.9 1.8L12 16.4l-1.8-4.9L5.3 9.7l4.9-1.8L12 3Z"></path>',
  };
  function icon(name, size, color, style) {
    return '<svg width="' + size + '" height="' + size + '" viewBox="0 0 24 24" fill="none" stroke="' + color +
      '" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"' + (style ? ' style="' + style + '"' : '') +
      ' aria-hidden="true">' + ICON_PATHS[name] + '</svg>';
  }

  // ---- Tiny DOM helper ------------------------------------------------------
  function h(tag, opts, kids) {
    var e = document.createElement(tag);
    opts = opts || {};
    if (opts.css) e.style.cssText = opts.css;
    if (opts.cls) e.className = opts.cls;
    if (opts.html != null) e.innerHTML = opts.html;
    if (opts.on) for (var ev in opts.on) e.addEventListener(ev, opts.on[ev]);
    if (opts.attrs) for (var a in opts.attrs) e.setAttribute(a, opts.attrs[a]);
    (kids || []).forEach(function (c) {
      if (c == null) return;
      e.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
    });
    return e;
  }

  // ---- Chat widget ----------------------------------------------------------
  function initChat(root) {
    var state = { profileId: 'bailey', messages: greeting(NV_PROFILES[0]), typing: false, asked: [] };
    var typingTimer = null;
    var profile = function () { return NV_PROFILES.filter(function (p) { return p.id === state.profileId; })[0]; };

    function bubble(msg, animate) {
      var isUser = msg.from === 'user';
      var kids = [];
      if (msg.level) {
        var lv = NV_LEVELS[msg.level];
        kids.push(h('span', {
          css: 'display:inline-flex;align-items:center;gap:7px;align-self:flex-start;background:' + lv.bg +
            ';color:' + lv.color + ';border-radius:999px;padding:4px 11px;font-family:var(--sn-display);font-weight:700;' +
            'font-size:11.5px;letter-spacing:0.03em;text-transform:uppercase',
        }, [
          h('span', { css: 'width:7px;height:7px;border-radius:999px;background:' + lv.color }),
          lv.label,
        ]));
      }
      if (msg.typing) {
        kids.push(h('span', { css: 'display:inline-flex;gap:5px;color:var(--sn-ink-60);padding:2px 0',
          html: '<span class="sn-dot"></span><span class="sn-dot"></span><span class="sn-dot"></span>' }));
      } else {
        msg.paras.forEach(function (t) { kids.push(h('span', {}, [t])); });
      }

      var inner = h('div', {
        css: 'max-width:80%;border-radius:18px;padding:12px 16px;font-size:14.5px;line-height:1.55;' +
          'background:' + (isUser ? 'var(--sn-forest)' : '#fff') + ';' +
          'color:' + (isUser ? 'var(--sn-cream)' : 'var(--sn-ink)') + ';' +
          'border:' + (isUser ? 'none' : '1px solid var(--sn-line)') + ';' +
          'border-bottom-right-radius:' + (isUser ? '5px' : '18px') + ';' +
          'border-bottom-left-radius:' + (isUser ? '18px' : '5px') + ';' +
          'display:flex;flex-direction:column;gap:9px',
      }, kids);

      return h('div', {
        cls: 'sn-msg-in' + (animate ? '' : ''),
        css: 'display:flex;justify-content:' + (isUser ? 'flex-end' : 'flex-start') + ';gap:10px',
      }, [
        isUser ? null : h('div', { css: 'flex-shrink:0;margin-top:2px', html: orb(30) }),
        inner,
      ]);
    }

    function renderMessages(scroll) {
      var box = root.querySelector('[data-nv-messages]');
      box.innerHTML = '';
      var items = state.messages.slice();
      if (state.typing) items.push({ from: 'nova', typing: true });
      items.forEach(function (m, i) { box.appendChild(bubble(m, i === items.length - 1)); });
      if (scroll !== false) box.scrollTop = box.scrollHeight;
    }

    function ask(topic) {
      if (state.typing) return;
      state.messages.push({ from: 'user', paras: [topic.q] });
      if (state.asked.indexOf(topic.id) === -1) state.asked.push(topic.id);
      state.typing = true;
      syncChips();
      renderMessages();
      clearTimeout(typingTimer);
      typingTimer = setTimeout(function () {
        var ans = topic.a(profile());
        state.messages.push({ from: 'nova', level: ans.level, paras: ans.paras });
        state.typing = false;
        syncChips();
        renderMessages();
      }, 850);
    }

    function switchProfile(id) {
      if (id === state.profileId) return;
      clearTimeout(typingTimer);
      state.typing = false;
      state.asked = [];
      state.profileId = id;
      state.messages = greeting(NV_PROFILES.filter(function (p) { return p.id === id; })[0]);
      syncProfile();
      syncChips();
      renderMessages();
    }

    function syncChips() {
      root.querySelectorAll('[data-nv-topic]').forEach(function (btn) {
        var id = btn.getAttribute('data-nv-topic');
        btn.style.background = state.asked.indexOf(id) !== -1 ? 'var(--sn-lime-soft)' : '#fff';
        btn.style.opacity = state.typing ? '0.5' : '1';
        btn.style.cursor = state.typing ? 'default' : 'pointer';
        btn.disabled = state.typing;
      });
    }

    function syncProfile() {
      var p = profile();
      root.querySelectorAll('[data-nv-profile]').forEach(function (btn) {
        var active = btn.getAttribute('data-nv-profile') === state.profileId;
        btn.style.border = active ? '2px solid var(--sn-forest)' : '2px solid var(--sn-line)';
        btn.style.background = active ? 'var(--sn-lime-soft)' : '#fff';
        btn.style.boxShadow = active ? '0 0 0 4px rgba(207,255,144,0.35)' : 'none';
      });
      var status = root.querySelector('[data-nv-status]');
      if (status) status.textContent = "24/7 · on " + p.name + "'s profile";
      var ph = root.querySelector('[data-nv-placeholder]');
      if (ph) ph.textContent = 'Ask Nova about ' + p.name + '…';
      var note = root.querySelector('[data-nv-note]');
      if (note) note.innerHTML = '<strong>Why it matters:</strong> ' + p.note;
      var build = root.querySelector('[data-nv-build]');
      if (build) build.textContent = 'Build ' + p.name + "'s profile";
    }

    // Build the interactive widget (replaces the static SSR snapshot).
    var p0 = profile();
    var header = h('div', { css: 'display:flex;align-items:center;gap:12px;padding:16px 22px;border-bottom:1px solid var(--sn-line);background:var(--sn-cream)' }, [
      h('div', { css: 'flex-shrink:0', html: orb(40) }),
      h('div', { css: 'flex:1' }, [
        h('div', { css: 'font-family:var(--sn-display);font-weight:700;font-size:16px' }, ['Nova']),
        h('div', { css: 'font-size:12.5px;color:var(--sn-ink-60);display:flex;align-items:center;gap:6px' }, [
          h('span', { css: 'width:7px;height:7px;border-radius:999px;background:#5B8C3E' }),
          ' ',
          h('span', { attrs: { 'data-nv-status': '' } }, ["24/7 · on " + p0.name + "'s profile"]),
        ]),
      ]),
      h('span', { cls: 'sn-eyebrow', css: 'color:var(--sn-forest);font-size:11px', html: icon('sparkle', 14, 'var(--sn-forest)') + ' AI vet' }),
    ]);

    var messages = h('div', { attrs: { 'data-nv-messages': '' }, css: 'flex:1;overflow-y:auto;padding:22px;display:flex;flex-direction:column;gap:14px;background:var(--sn-cream)' });

    var chips = h('div', { css: 'padding:12px 16px;border-top:1px solid var(--sn-line);display:flex;flex-wrap:wrap;gap:8px;background:#fff' },
      NV_TOPICS.map(function (t) {
        return h('button', {
          attrs: { 'data-nv-topic': t.id },
          css: 'cursor:pointer;border-radius:999px;padding:8px 14px;font-size:13px;font-weight:600;font-family:var(--sn-display);border:1.5px solid var(--sn-line);background:#fff;color:var(--sn-forest);opacity:1',
          on: { click: function () { ask(t); } },
        }, [t.label]);
      }));

    var inputRow = h('div', { css: 'padding:14px 16px;border-top:1px solid var(--sn-line);display:flex;gap:10px;align-items:center;background:#fff' }, [
      h('div', { attrs: { 'data-nv-placeholder': '' }, css: 'flex:1;background:var(--sn-cream);border-radius:999px;padding:12px 18px;font-size:14px;color:var(--sn-ink-60);border:1px solid var(--sn-line)' }, ['Ask Nova about ' + p0.name + '…']),
      h('button', { css: 'border:none;cursor:pointer;width:44px;height:44px;border-radius:999px;background:var(--sn-forest);display:inline-flex;align-items:center;justify-content:center;flex-shrink:0', html: icon('arrow', 20, 'var(--sn-cream)') }),
    ]);

    var leftCol = h('div', { css: 'display:flex;flex-direction:column;height:600px;border-right:1px solid var(--sn-line)' }, [header, messages, chips, inputRow]);

    var profilePanel = h('div', { css: 'display:flex;flex-direction:column;gap:10px' },
      NV_PROFILES.map(function (p) {
        var active = p.id === state.profileId;
        return h('button', {
          attrs: { 'data-nv-profile': p.id },
          css: 'cursor:pointer;text-align:left;display:flex;align-items:center;gap:12px;border-radius:16px;padding:12px 14px;' +
            'border:' + (active ? '2px solid var(--sn-forest)' : '2px solid var(--sn-line)') + ';' +
            'background:' + (active ? 'var(--sn-lime-soft)' : '#fff') + ';' +
            'box-shadow:' + (active ? '0 0 0 4px rgba(207,255,144,0.35)' : 'none'),
          on: { click: function () { switchProfile(p.id); } },
        }, [
          pawDisc(42, p.bg),
          h('div', {}, [
            h('div', { css: 'font-family:var(--sn-display);font-weight:700;font-size:15.5px' }, [p.name]),
            h('div', { css: 'font-size:12.5px;color:var(--sn-ink-60)' }, [p.meta]),
          ]),
        ]);
      }));

    var rightCol = h('div', { css: 'padding:26px 26px;background:#fff;display:flex;flex-direction:column' }, [
      h('h3', { css: 'font-size:19px;margin-bottom:6px' }, ['Whose health are we talking about?']),
      h('p', { css: 'font-size:13.5px;color:var(--sn-ink-60);line-height:1.5;margin-bottom:18px' }, ["Nova shapes every answer around the pet's age, breed and history. Switch pet - watch the same question get a different answer."]),
      profilePanel,
      h('div', { css: 'margin-top:auto;padding-top:22px' }, [
        h('div', { cls: 'sn-msg-in', attrs: { 'data-nv-note': '' }, css: 'background:var(--sn-cream-2);border-radius:16px;padding:14px 16px;font-size:13px;color:var(--sn-ink);line-height:1.5;display:flex;gap:10px',
          html: icon('sparkle', 16, 'var(--sn-forest)', 'flex-shrink:0;margin-top:1px') + '<span><strong>Why it matters:</strong> ' + p0.note + '</span>' }),
        h('a', { cls: 'sn-btn sn-btn--primary', css: 'width:100%;justify-content:center;margin-top:12px' }, [
          h('span', { attrs: { 'data-nv-build': '' } }, ['Build ' + p0.name + "'s profile"]),
          ' ',
          h('span', { css: 'display:inline-flex', html: icon('arrow', 18, 'currentColor') }),
        ]),
      ]),
    ]);

    var grid = h('div', { css: 'display:grid;grid-template-columns:1.5fr 1fr;border-radius:28px;overflow:hidden;border:1px solid var(--sn-line);box-shadow:0 30px 70px -45px rgba(28,25,23,0.5);background:#fff' }, [leftCol, rightCol]);

    root.innerHTML = '';
    root.appendChild(grid);
    renderMessages(false);
  }

  // The FAQ accordion is handled by the shared converted/_assets/sn-accordion.js.
  // Answers are rendered into the static body.html (for SEO/crawlability), so this
  // file only owns the chat widget.

  // ---- Boot -----------------------------------------------------------------
  function boot() {
    var page = document.getElementById('sn-online-vet-advice');
    if (!page) return;
    var chat = page.querySelector('[data-sn-widget="NovaChat"]');
    if (chat) initChat(chat);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
